import { useCallback, useEffect, useState } from 'react';

/* 
This is a singleton approach  - only one audio stream is created.
By doing so, the data for that one stream can be referenced anywhere 
in the code if calling the hook in multiple places. 

We get Javascript (in a global scope) to create the stream and using 
a callback, will update the stream properties so the updated values 
are accessible in the React hook.

There are some Safari specifics to make sure that the stream does 
start and work correctly. This doesn't impact other browsers.
*/

let globalAudioContext: AudioContext | null = null;
let globalStream: MediaStream | null = null;
let globalSource: MediaStreamAudioSourceNode | null = null;
let globalAnalyser: AnalyserNode | null = null;
let globalGain: GainNode | null = null;
let globalAnimationFrameId: number | null = null;
let globalWakeLock: AudioBufferSourceNode | null = null;

let isListening = false;
let currentLevel = 0;
let activeStreamToken: object | null = null;

const riseSpeed = 0.4;
const fallSpeed = 0.1;
const delayTimeBeforeFall = 500;

const listenerProps = new Set<(state: { isListening: boolean; level: number }) => void>();

const updateListenerProps = () => {
  listenerProps.forEach((callback) => {
    callback({ isListening, level: currentLevel });
  });
};

const stopAudioIndicator = () => {
  activeStreamToken = null;

  if (globalAnimationFrameId) cancelAnimationFrame(globalAnimationFrameId);
  if (globalStream) globalStream.getTracks().forEach((t) => t.stop());
  if (globalWakeLock) {
    try {
      globalWakeLock.stop();
    } catch (error: any) {
      if (error.name !== 'InvalidStateError') {
        console.warn('Wake lock failed to stop:', error);
      }
    }
    globalWakeLock.disconnect();
  }
  if (globalSource) globalSource.disconnect();
  if (globalGain) globalGain.disconnect();
  if (globalAudioContext?.state !== 'closed') globalAudioContext?.close();

  globalAnimationFrameId = null;
  globalAudioContext = null;
  globalSource = null;
  globalStream = null;
  globalAnalyser = null;
  globalGain = null;
  globalWakeLock = null;

  isListening = false;
  currentLevel = 0;

  updateListenerProps();
};

const startAudioIndicator = async () => {
  // Initial cleanup
  stopAudioIndicator();

  // Create a unique token to identify the current stream
  const latestStreamToken = {};
  activeStreamToken = latestStreamToken;

  // Used to safely handle cleanup without affecting the global versions
  let localAudioContext: AudioContext | null = null;
  let localStream: MediaStream | null = null;
  let localWakeLock: AudioBufferSourceNode | null = null;

  localAudioContext = new AudioContext();

  try {
    globalAudioContext = localAudioContext;

    // Play a silent buffer immediately
    // Wakes up the audio engine in Safari/iOS and keeps it running
    const buffer = localAudioContext.createBuffer(1, 1, 22050);
    localWakeLock = localAudioContext.createBufferSource();
    localWakeLock.buffer = buffer;
    localWakeLock.connect(localAudioContext.destination);
    localWakeLock.start(0);
    globalWakeLock = localWakeLock;

    // Use "rawest" audio possible by disabling processing
    localStream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: false, autoGainControl: false, noiseSuppression: false }
    });

    // Prevent keeping microphone access on if stream has changed
    if (activeStreamToken !== latestStreamToken) {
      if (localStream) localStream.getTracks().forEach((t) => t.stop());
      if (localAudioContext.state !== 'closed') localAudioContext.close();
      return;
    }

    // Mic -> Analyser -> Gain (muted) -> Destination
    // Prevent Analyser from being paused by playing a muted sound
    // Connecting to destination (speakers) keeps Safari audio processing active
    globalStream = localStream;
    globalSource = localAudioContext.createMediaStreamSource(localStream);
    globalAnalyser = localAudioContext.createAnalyser();
    globalGain = localAudioContext.createGain();

    globalAnalyser.fftSize = 512;
    globalAnalyser.smoothingTimeConstant = 0.8;
    globalGain.gain.value = 0;

    globalSource.connect(globalAnalyser);
    globalAnalyser.connect(globalGain);
    globalGain.connect(localAudioContext.destination);

    isListening = true;
    const dataArray = new Uint8Array(globalAnalyser.frequencyBinCount);
    let lastRiseTime = Date.now();

    const updateMeter = () => {
      if (activeStreamToken !== latestStreamToken) return;

      if (globalAudioContext?.state === 'suspended') {
        globalAudioContext.resume().catch(() => {
          isListening = false;
          console.error('Audio context suspended - failed to resume');
        });
      }

      globalAnalyser?.getByteTimeDomainData(dataArray);

      const averageAmplitude = dataArray.reduce((a, v) => a + Math.abs(v - 128), 0) / dataArray.length;
      const target = averageAmplitude / 8;

      if (target > currentLevel) {
        lastRiseTime = Date.now();
        currentLevel = currentLevel * (1 - riseSpeed) + target * riseSpeed;
      } else {
        const currentRiseTime = Date.now();
        const elapsedTime = currentRiseTime - lastRiseTime;

        if (elapsedTime > delayTimeBeforeFall) {
          currentLevel = currentLevel * (1 - fallSpeed) + target * fallSpeed;
        }
        // Else hold at the current sound level
      }
      updateListenerProps();

      globalAnimationFrameId = requestAnimationFrame(updateMeter);
    };

    updateMeter();
    updateListenerProps();
  } catch (err) {
    console.error(err);
    stopAudioIndicator();
  }
};

export function useAudioIndicator() {
  const [indicator, setIndicator] = useState({ isListening, level: currentLevel });

  useEffect(() => {
    const updateIndicator = (updateState: { isListening: boolean; level: number }) => {
      // Performance optimisation - only update when the value changes
      setIndicator((prevValue) => {
        if (prevValue.isListening === updateState.isListening && prevValue.level === updateState.level)
          return prevValue;
        else return updateState;
      });
    };
    listenerProps.add(updateIndicator);

    return () => {
      listenerProps.delete(updateIndicator);

      if (listenerProps.size === 0) {
        stopAudioIndicator();
      }
    };
  }, []);

  const start = useCallback(() => startAudioIndicator(), []);
  const stop = useCallback(() => stopAudioIndicator(), []);

  return {
    isListening: indicator.isListening,
    level: indicator.level,
    startAudioIndicator: start,
    stopAudioIndicator: stop
  };
}
